/**
 * TriageProjectModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A modal component for displaying detailed triage project information with:
 * - Draggable functionality for enhanced UX
 * - Fullscreen capability for better project viewing
 * - Comprehensive project details display
 * - Keyboard navigation (Escape key support)
 * - Position tracking for drag operations
 * - Dynamic styling and positioning
 * - Structured data presentation for triage items
 * 
 * @param {Object} data - Triage project data containing all project details
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} allowMove - Whether the modal can be dragged
 * @param {Object} style - Additional CSS styles for the modal
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useState, useRef, useEffect } from 'react';
import './TriageModal.css';
import Draggable from 'react-draggable';
import apiHelper from '../utils/apiHelper';
import { useProfile } from '../contexts/ProfileContext';

/**
 * TriageProjectModal component for displaying detailed triage project information
 * with draggable functionality and fullscreen support
 */
function TriageProjectModal({ data, show, onClose, allowMove = false, style }) {
    const { profile } = useProfile();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const draggableNodeRef = useRef(null);
    const draggablePositionRef = useRef({ x: 0, y: 0 });

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        if (!isFullScreen) {
            draggablePositionRef.current = { x: 0, y: 0 }; // Reset position when entering fullscreen
        }
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
        if (draggableNodeRef.current && isFullScreen) {
            draggableNodeRef.current.style.transform = ''; // Remove transform applied by Draggable
        }
    }, [isFullScreen]);

    useEffect(() => {
        if (show && profile?.email) {
            apiHelper.logModalOpen(profile.email, 'TriageProjectModal', data?.Title || data?.title || 'Project', 'TriageProject');
        }
    }, [show, profile?.email]);

    if (!show) return null;

    return (
        <Draggable
            handle=".modal-header"
            nodeRef={draggableNodeRef}
            defaultPosition={draggablePositionRef.current}
            onStop={(e, data) => {
                draggablePositionRef.current = { x: data.x, y: data.y }; // Track the position after dragging
            }}
            disabled={isFullScreen || !allowMove}
        >
            <div
                ref={draggableNodeRef}
                className={`modal triageModal display-block ${isFullScreen ? 'fullscreen' : ''} ${!allowMove ? 'nomove' : ''}`}
                style={style}
            > <div className="modal-content">
                    <div className="modal-header">
                        <span id="modalTitle">Triage Item Details</span>
                        <span className="modalfullscreen" onClick={toggleFullScreen}></span>
                        <span className="modalclose" onClick={onClose}></span>
                    </div>
                    <div className="modal-body" id="triageModalContent">
                        <div className="triageModalcontent">
                            <div className="item">
                                <div className="label headeritem">Name</div>
                                <div className="value">
                                    <span id="topicParent">{data?.name || "N/A"}</span>
                                    <span id="topicChild">{data?.childName || "N/A"}</span>
                                </div>
                            </div>
                            <div className="item">
                                <div className="label headeritem">Key Identifiers</div>
                                <div className="value"><span id="keyIdentifiers"> {data?.keyIdentifiers ? data.keyIdentifiers.replace(/;/g, ",") : "N/A"} </span></div>
                            </div>
                            <div className="item">
                                <div className="label headeritem">Leading Body</div>
                                <div className="value"><span id="leadingBody">{data?.leadingBody || "N/A"}</span></div>
                            </div>
                            <div className="item">
                                <div className="label headeritem">Spirit & Focus</div>
                                <div className="value"><span id="spiritFocus">{data?.spiritFocus || "N/A"}</span></div>
                            </div>
                            {/* <div className="item">
                                <div className="label headeritem">Description</div>
                                <div className="value"><span id="shortDescription">{data?.shortDescription || "N/A"}</span></div>
                            </div> */}
                            <div className="item">
                                <div className="label headeritem">Commencement Date</div>
                                <div className="value">
                                    <span>{data?.dateItemAddedtoTriage || "N/A"}</span>
                                </div>
                            </div>
                            <div className="item">
                                <div className="label headeritem">Completion Date</div>
                                <div className="value">
                                    <span>{data?.completionDate || "N/A"}</span>
                                </div>
                            </div>
                            <div className="item">
                                <div className="label headeritem">Working Group</div>
                                <div className="value"><span id="workingGroup">{data?.workingGroup || "N/A"}</span></div>
                            </div>
                           

                            <div className="item lastitem">
                                <div className="label headeritem">Expected Output</div>
                                <div className="value">
                                    <span id="submission">
                                        {data?.expectedActualOutput}
                                    </span>
                                </div>
                            </div>

                           

                            
                        </div>
                    </div>
                </div>
            </div>
        </Draggable>
    );
}

export default TriageProjectModal;
